import { atom, useAtomValue, useSetAtom } from 'jotai';
import React, { FC, lazy, useEffect } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation, useParams } from 'react-router';

import { useAuthStateValue, useIsAuthenticated } from './auth/authentication';
import { GlobalNavigationDrawer } from './components/GlobalNavigationDrawer';
import { NavigationDrawer } from './components/NavigationDrawer/NavigationDrawer';
import { AxlePage } from './components/layout/AxlePage';
import { PylonWidget } from './components/support/PylonWidget';
import { DIALOG_FLAGS } from './dialogs/useDialogShellFlags';
import { AxleErrorBoundary, ErrorFallback } from './fetching/errorBoundaries';
import { AxleSuspense, PAGE_BODY_SKELETON_HEIGHT } from './fetching/suspense';
import { NotFoundPage } from './pages/NotFoundPage';
import { PATIENT_DETAILS_LAYOUT_FLAGS } from './pages/PatientDetailsPage/patientDetailsFlags';
import { FeatureFlag } from './types/featureFlag';
import { isLocal } from './utils/axleEnvironment';
import { usePostAuthRedirect, useRecordLastAuthedPage } from './utils/lastAuthedPage';
import { useQueryParamReadonly } from './utils/queryParams';
import { Component, DefiniteComponent } from './utils/reactTypes';
import {
    useFeatureFlagEnabled,
    useFeatureFlagEnabledQuery,
    useFeatureFlagsEnabled,
} from './utils/useFeatureFlagEnabled';

// Lazy load all page components for code splitting
const AllVisitsPage = lazy(() =>
    import('./pages/AllVisitsPage/AllVisitsPage').then((m) => ({ default: m.AllVisitsPage })),
);
const BlocksPage = lazy(() =>
    import('./pages/BlocksPage').then((m) => ({ default: m.BlocksPage })),
);
const BookingFlow = lazy(() =>
    import('./pages/BookingFlow/BookingFlow').then((m) => ({ default: m.BookingFlow })),
);
const CreateServiceAreaPage = lazy(() =>
    import('./pages/CRUDServiceAreaPages').then((m) => ({ default: m.CreateServiceAreaPage })),
);
const EditOrViewServiceAreaPage = lazy(() =>
    import('./pages/CRUDServiceAreaPages').then((m) => ({ default: m.EditOrViewServiceAreaPage })),
);
const ClinicianDetailsPage = lazy(() =>
    import('./pages/ClinicianDetailsPage/ClinicianDetailsPage').then((m) => ({
        default: m.ClinicianDetailsPage,
    })),
);
const CliniciansPage = lazy(() =>
    import('./pages/CliniciansPage/CliniciansPage').then((m) => ({
        default: m.CliniciansPage,
    })),
);
const DashboardPage = lazy(() =>
    import('./pages/DashboardPage/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const DemandPage = lazy(() =>
    import('./pages/DemandPage/DemandPage').then((m) => ({ default: m.DemandPage })),
);
const ForgotPasswordPage = lazy(() =>
    import('./pages/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })),
);
const GoogleMapTestingShimPage = lazy(() =>
    import('./components/mapping/GoogleMap/GoogleMapTestingShimPage').then((m) => ({
        default: m.GoogleMapTestingShimPage,
    })),
);
const InsightsPage = lazy(() =>
    import('./pages/InsightsPage').then((m) => ({ default: m.InsightsPage })),
);
const IntakeCapacityPage = lazy(() =>
    import('./pages/IntakeCapacityPage').then((m) => ({ default: m.IntakeCapacityPage })),
);
const LoginPage = lazy(() => import('./pages/LoginPage').then((m) => ({ default: m.LoginPage })));
const MangleDbPage = lazy(() =>
    import('./pages/MangleDbPage').then((m) => ({ default: m.MangleDbPage })),
);
const OrderDetailsPage = lazy(() =>
    import('./pages/OrderDetailsPage/OrderDetailsPage').then((m) => ({
        default: m.OrderDetailsPage,
    })),
);
const OrdersPage = lazy(() =>
    import('./pages/OrdersPage/OrdersPage').then((m) => ({ default: m.OrdersPage })),
);
const PatientDetailsLayout = lazy(() =>
    import('./pages/PatientDetailsPage/PatientDetailsLayout').then((m) => ({
        default: m.PatientDetailsLayout,
    })),
);
const PatientPreferencesTab = lazy(() =>
    import('./pages/PatientDetailsPage/PatientVisitPreferences/PatientPreferencesTab').then(
        (m) => ({ default: m.PatientPreferencesTab }),
    ),
);
const PatientProfileTab = lazy(() =>
    import('./pages/PatientDetailsPage/PatientProfileTab/PatientProfileTab').then((m) => ({
        default: m.PatientProfileTab,
    })),
);
const PatientSchedulingTab = lazy(() =>
    import('./pages/PatientDetailsPage/PatientSchedulingTab/PatientSchedulingTab').then((m) => ({
        default: m.PatientSchedulingTab,
    })),
);
const PatientPortalVisitsPage = lazy(() =>
    import('./pages/PatientPortalPages/PatientPortalVisitsPage').then((m) => ({
        default: m.PatientPortalVisitsPage,
    })),
);
const PatientsPage = lazy(() =>
    import('./pages/PatientsPage/PatientsPage').then((m) => ({ default: m.PatientsPage })),
);
const ReportsPage = lazy(() =>
    import('./pages/ReportsPage').then((m) => ({ default: m.ReportsPage })),
);
const ResetPasswordPage = lazy(() =>
    import('./pages/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })),
);
const RouteOptimizerAdminPage = lazy(() =>
    import('./pages/RouteOptimizerAdminPage/RouteOptimizerAdminPage').then((m) => ({
        default: m.RouteOptimizerAdminPage,
    })),
);
const RouteOptimizerPage = lazy(() =>
    import('./pages/RouteOptimizerPage/RouteOptimizerPage').then((m) => ({
        default: m.RouteOptimizerPage,
    })),
);
const SSOCallbackPage = lazy(() =>
    import('./pages/SSOCallbackPage').then((m) => ({ default: m.SSOCallbackPage })),
);
const SSOLoginPage = lazy(() =>
    import('./pages/SSOLoginPage').then((m) => ({ default: m.SSOLoginPage })),
);
const ClinicianSchedulesPage = lazy(() =>
    import('./pages/SchedulePage/ClinicianSchedulesPage').then((m) => ({
        default: m.ClinicianSchedulesPage,
    })),
);
const ScheduleVisitPage = lazy(() =>
    import('./pages/ScheduleVisitPages/ScheduleVisitPage').then((m) => ({
        default: m.ScheduleVisitPage,
    })),
);
const ScratchPage = lazy(() =>
    import('./pages/ScratchPage').then((m) => ({ default: m.ScratchPage })),
);
const ServiceAreasPage = lazy(() =>
    import('./pages/ServiceAreasPage').then((m) => ({ default: m.ServiceAreasPage })),
);
const UserSettingsPage = lazy(() =>
    import('./pages/UserSettingsPage/UserSettingsPage').then((m) => ({
        default: m.UserSettingsPage,
    })),
);
const SettingsPage = lazy(() =>
    import('./pages/SettingsPage/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);
const SupplyPage = lazy(() =>
    import('./pages/SupplyPage/SupplyPage').then((m) => ({ default: m.SupplyPage })),
);
const TaskDetailsPage = lazy(() =>
    import('./pages/TaskDetailsPage').then((m) => ({ default: m.TaskDetailsPage })),
);
const TerritoriesPage = lazy(() =>
    import('./pages/TerritoriesPage/TerritoriesPage').then((m) => ({ default: m.TerritoriesPage })),
);
const VisitBookingPage = lazy(() =>
    import('./pages/VisitBooking/VisitBookingPage').then((m) => ({ default: m.VisitBookingPage })),
);
const VisitDetailsPage = lazy(() =>
    import('./pages/VisitDetailsPage/VisitDetailsPage').then((m) => ({
        default: m.VisitDetailsPage,
    })),
);
const VisitTravelPathDebugPage = lazy(() =>
    import('./pages/VisitDetailsPage/VisitTravelPathDebugPage').then((m) => ({
        default: m.VisitTravelPathDebugPage,
    })),
);
const VisitOrderDetailsPage = lazy(() =>
    import('./pages/VisitOrderDetailsPage').then((m) => ({ default: m.VisitOrderDetailsPage })),
);
const VisitOrdersPage = lazy(() =>
    import('./pages/VisitOrdersPage').then((m) => ({ default: m.VisitOrdersPage })),
);
const VisitRequestDetailsPage = lazy(() =>
    import('./pages/VisitRequestDetailsPage/VisitRequestDetailsPage').then((m) => ({
        default: m.VisitRequestDetailsPage,
    })),
);
const LegacyPatientDetailsPage = lazy(() =>
    import('./pages/legacy/LegacyPatientDetailsPage/LegacyPatientDetailsPage').then((m) => ({
        default: m.LegacyPatientDetailsPage,
    })),
);
const LegacyPatientsPage = lazy(() =>
    import('./pages/legacy/LegacyPatientsPage/LegacyPatientsPage').then((m) => ({
        default: m.LegacyPatientsPage,
    })),
);
const LegacyVisitsPage = lazy(() =>
    import('./pages/legacy/LegacyVisitsPage/LegacyVisitsPage').then((m) => ({
        default: m.LegacyVisitsPage,
    })),
);
const QueryTestingShimPage = lazy(() =>
    import('./pages/shims/QueryTestShimPage').then((m) => ({ default: m.QueryTestingShimPage })),
);
const SentryTestingShimPage = lazy(() =>
    import('./pages/shims/SentryTestingShimPage').then((m) => ({
        default: m.SentryTestingShimPage,
    })),
);

type PageComponent = React.LazyExoticComponent<() => Component> | (() => Component);

type PageProps = {
    component: PageComponent;
    condition?: boolean;
    path: string;
};

type SectionProps = {
    component: PageComponent;
    path: string;
};

/* A page, rendered inside its own error boundary and Suspense boundary. Both sit below the
   layout, so a page that fails or is still loading leaves the layout around it — the
   navigation drawer included — mounted and usable.

   The route path keys the error boundary so that navigating to another page resets it;
   otherwise it stays stuck in error mode. See: https://stackoverflow.com/q/59056211/1292652 */
const RoutePage = ({
    component: Component,
    condition = true,
    path,
}: PageProps): DefiniteComponent => {
    if (!condition) {
        return <NotFoundPage />;
    }

    return (
        <AxleErrorBoundary
            key={path}
            reload={true}
            fallback={
                <AxlePage>
                    <ErrorFallback />
                </AxlePage>
            }
        >
            {/* Pages are encouraged to have their own inner Suspense elements!!
             *   It looks prettier if the title of the page renders immediately but the details
             *   below them have a spinner, than if the title takes time to load too.
             */}
            <AxleSuspense customFallback={<AxlePage loading />}>
                <Component />
            </AxleSuspense>
        </AxleErrorBoundary>
    );
};

/* Route for a page. The path both matches the route and identifies the page to RoutePage. */
const pageRoute = ({ component, condition, path }: PageProps): DefiniteComponent => (
    <Route
        path={path}
        element={<RoutePage path={path} component={component} condition={condition} />}
    />
);

/* A section of a page, rendered in a layout's outlet inside its own error boundary and Suspense
   boundary. Both sit below the layout, so a section that fails or is still loading leaves the
   layout's own chrome — its headers, rails and tabs — mounted and usable.

   The route path keys the error boundary so that navigating to another section resets it;
   otherwise it stays stuck in error mode. */
const RouteSection = ({ component: Component, path }: SectionProps): DefiniteComponent => (
    <AxleErrorBoundary key={path} reload={true} fallback={<ErrorFallback />}>
        <AxleSuspense skeletonHeight={PAGE_BODY_SKELETON_HEIGHT}>
            <Component />
        </AxleSuspense>
    </AxleErrorBoundary>
);

/* Route for a section of a page. The path is relative to the layout route it sits under. */
const sectionRoute = ({ component, path }: SectionProps): DefiniteComponent => (
    <Route path={path} element={<RouteSection path={path} component={component} />} />
);

/* Routes that resolve the same way with or without a session, rendered in both route trees.
   Each page stands alone, so they sit outside the authenticated layout and its navigation
   drawer. */
const publicRoutes = (): DefiniteComponent => (
    <>
        {pageRoute({
            path: '/patient-portal/:patientUuid/visits',
            component: PatientPortalVisitsPage,
        })}
        {pageRoute({ path: '/reset-password', component: ResetPasswordPage })}
        {pageRoute({ path: '/forgot-password', component: ForgotPasswordPage })}
    </>
);

// Whether this tab has held a session. A tab that has keeps the page it was on out of
//  the sign-in URL: that page is stored per tab and scoped to the user it belonged to
//  (see useRecordLastAuthedPage), so a tab whose session ended looks like a clean
//  sign-in page. A tab that has never held one has nowhere else to keep where the user
//  was headed, so it falls back to the URL.
const hasHeldSessionAtom = atom(false);

// Builds the `?redirect=` suffix for the sign-in URL from the current location, so that
//  signing in returns the user to the page they asked for.
const useRedirectRoute = () => {
    const hasHeldSession = useAtomValue(hasHeldSessionAtom);
    const location = useLocation();

    if (hasHeldSession) {
        return '';
    }

    const redirectEncoded = encodeURIComponent(location.pathname + location.search);
    // %2F is encoded "/" and does not need a redirect
    return redirectEncoded !== '%2F' ? `?redirect=${redirectEncoded}` : '';
};

const RedirectToSignin: FC = () => {
    const redirectRoute = useRedirectRoute();

    return <Navigate to={`/sign-in${redirectRoute}`} replace />;
};

/* When an already-authenticated user lands on a login-entry path, send them on
   to their intended destination instead of falling through to Not Found. This
   most commonly happens right after signing in: setting the auth state swaps
   the unauthenticated routes out for AuthedRoutes (unmounting the login page)
   before the login page itself can navigate away, leaving the URL on e.g.
   /sign-in?redirect=… with no matching authed route. A destination asked for in
   the URL lives in the `redirect` query param for /sign-in and /sso, and in
   `state` for the SSO callback (where it survives the WorkOS round-trip);
   otherwise the user goes back to the page this tab's last session ended on.
   A sign-in in another tab reaches this one as a `storage` event that flips the
   auth state, so each tab resolves its own destination this way. */
const RedirectPostAuth: FC<{ param: string }> = ({ param }) => {
    const authState = useAuthStateValue();
    const target = usePostAuthRedirect(useQueryParamReadonly(param), authState?.uuid);

    return <Navigate to={target} replace />;
};

/* Records where an authenticated user is, so that a session ending in this tab or in a
   sibling one has a page to return to. This is a leaf component so that subscribing to
   the location re-renders nothing but itself: the route tree above it stays out of the
   navigation render path. The non-suspending flag query keeps it that way, since a
   suspending one would push a fallback up to the nearest boundary above the routes.

   Recording is the only writer, so gating it here turns the whole behaviour off: with
   nothing stored, every post-auth destination falls back to the one asked for in the
   URL, and then to the landing page. */
const LastAuthedPageRecorder: FC<{ userUuid: string | undefined }> = ({ userUuid }) => {
    const { enabled: recordsLastAuthedPage } = useFeatureFlagEnabledQuery(
        FeatureFlag.DASHBOARD_STORE_TAB_STATE_ON_LOGOUT,
    );

    // Without a UUID there is no identity to scope a page to, so nothing is recorded.
    useRecordLastAuthedPage(recordsLastAuthedPage ? userUuid : undefined);

    return null;
};

function LegacyPatientRedirect() {
    const { patientId } = useParams();
    return <Navigate to={`/patients/${patientId}`} replace />;
}

// The patient's own path, and anything under it that is not a section. Search and hash come
// along, since the episode selection and the linked-visit anchor both travel that way.
function PatientDetailsIndexRedirect() {
    const { patientId } = useParams();
    const { search, hash } = useLocation();

    return <Navigate to={`/patients/${patientId}/scheduling${search}${hash}`} replace />;
}

// Anything under the patient that is not the patient's own path.
function PatientDetailsRedirect(): DefiniteComponent {
    const { patientId } = useParams();
    const { search, hash } = useLocation();

    return <Navigate to={`/patients/${patientId}${search}${hash}`} replace />;
}

function VisitRescheduleRedirect() {
    const { visitId } = useParams();
    return <Navigate to={`/visits/${visitId}/schedule/`} replace />;
}

/* Layout for the authenticated pages. The navigation drawer is declared once here and the
   matched page renders in the outlet, so the drawer stays mounted across every navigation
   between pages under it — including navigations that pass through a redirect route.

   A user without a session is sent to sign-in rather than to any page under the layout. */
const AuthenticatedLayout = (): DefiniteComponent => {
    const isAuthenticated = useIsAuthenticated();
    const hasGlobalNavigationV2 = useFeatureFlagEnabled(FeatureFlag.GLOBAL_NAVIGATION_V2, {
        skip: !isAuthenticated,
    });

    if (!isAuthenticated) {
        return <RedirectToSignin />;
    }

    return (
        <>
            {hasGlobalNavigationV2 ? <GlobalNavigationDrawer /> : <NavigationDrawer />}
            <PylonWidget />
            <Outlet />
        </>
    );
};

export const AuthedRoutes = () => {
    const isAuthenticated = useIsAuthenticated();

    /* The dialog flags are read here rather than only where a dialog opens. A dialog is
     * rendered inside a Suspense boundary with a null fallback, so a flags read of its own
     * would show nothing at all until it resolved, and only then start fetching the dialog's
     * data. Read with the routes, they are cached by the time anything is clicked.
     */
    const {
        hasNewVisitsList,
        hasNewPatientsList,
        hasNewPatientsDetails,
        hasNewCliniciansList,
        layoutV2: hasPatientDetailsLayoutV2,
    } = useFeatureFlagsEnabled(
        {
            hasNewVisitsList: FeatureFlag.VISITS_LIST_PAGE_V2,
            hasNewPatientsList: FeatureFlag.PATIENTS_LIST_PAGE_V2,
            hasNewPatientsDetails: FeatureFlag.PATIENT_DETAILS_PAGE_V2,
            hasNewCliniciansList: FeatureFlag.CLINICIANS_LIST_PAGE_V2,
            ...PATIENT_DETAILS_LAYOUT_FLAGS,
            ...DIALOG_FLAGS,
        },
        { skip: !isAuthenticated },
    );
    return (
        <Routes>
            {/* Login-entry paths: an authenticated user here (e.g. just after
                signing in, while the URL is still on the login page) is bounced
                to their intended destination rather than the Not Found fallback. */}
            <Route path='/sign-in' element={<RedirectPostAuth param='redirect' />} />
            <Route path='/sso' element={<RedirectPostAuth param='redirect' />} />
            <Route path='/sso-callback' element={<RedirectPostAuth param='state' />} />
            {publicRoutes()}
            {/* Partner admin pages (authenticated). */}
            {/* For simplicity, let's make the ordering here match the ordering of pages in the navigation drawer. */}
            <Route element={<AuthenticatedLayout />}>
                {/* Dashboard is the root route page, and will internally automatically redirect to the visits page if the dashboard feature flag is not enabled */}
                {pageRoute({ path: '/', component: DashboardPage })}
                {pageRoute({ path: '/visits/:visitId/schedule/', component: ScheduleVisitPage })}
                <Route path='/visits/reschedule/:visitId' element={<VisitRescheduleRedirect />} />
                {pageRoute({ path: '/visits/create', component: VisitBookingPage })}
                {pageRoute({ path: '/visits/:visitId', component: VisitDetailsPage })}
                {pageRoute({
                    path: '/visits',
                    component: hasNewVisitsList ? AllVisitsPage : LegacyVisitsPage,
                })}
                {pageRoute({
                    path: '/visit-requests/:visitRequestId',
                    component: VisitRequestDetailsPage,
                })}
                {pageRoute({ path: '/route-optimizer/admin', component: RouteOptimizerAdminPage })}
                {pageRoute({ path: '/route-optimizer', component: RouteOptimizerPage })}
                {pageRoute({ path: '/booking', component: BookingFlow })}
                {hasNewPatientsDetails ? (
                    /* The patient details shell is declared once as a layout and each section
                       renders in its outlet. */
                    <Route
                        path='/patients/:patientId'
                        element={
                            <RoutePage
                                path='/patients/:patientId'
                                component={PatientDetailsLayout}
                            />
                        }
                    >
                        {hasPatientDetailsLayoutV2 ? (
                            <>
                                <Route
                                    index
                                    element={
                                        <RouteSection
                                            path='scheduling'
                                            component={PatientSchedulingTab}
                                        />
                                    }
                                />
                                <Route path='*' element={<PatientDetailsRedirect />} />
                            </>
                        ) : (
                            <>
                                <Route index element={<PatientDetailsIndexRedirect />} />
                                {sectionRoute({
                                    path: 'scheduling',
                                    component: PatientSchedulingTab,
                                })}
                                {sectionRoute({
                                    path: 'preferences',
                                    component: PatientPreferencesTab,
                                })}
                                {sectionRoute({ path: 'profile', component: PatientProfileTab })}
                                <Route path='*' element={<PatientDetailsIndexRedirect />} />
                            </>
                        )}
                    </Route>
                ) : (
                    <>
                        {pageRoute({
                            path: '/patients/:patientId',
                            component: LegacyPatientDetailsPage,
                        })}
                        {/* Redirect legacy patient detail subroutes to base page */}
                        <Route
                            path='/patients/:patientId/:subroute'
                            element={<LegacyPatientRedirect />}
                        />
                    </>
                )}
                {pageRoute({
                    path: '/patients',
                    component: hasNewPatientsList ? PatientsPage : LegacyPatientsPage,
                })}
                {/* Direct URLs to legacy versions of pages when feature flags are enabled,
                    for ease of debugging and reference. */}
                {hasNewPatientsDetails &&
                    pageRoute({
                        path: '/legacy/patients/:patientId',
                        component: LegacyPatientDetailsPage,
                    })}
                {hasNewPatientsList &&
                    pageRoute({ path: '/legacy/patients', component: LegacyPatientsPage })}
                {hasNewVisitsList &&
                    pageRoute({ path: '/legacy/visits', component: LegacyVisitsPage })}
                {hasNewCliniciansList &&
                    pageRoute({ path: '/legacy/clinicians', component: SupplyPage })}
                {/* New Orders Page routes - will check for feature flag inside the page to verify access. */}
                {pageRoute({ path: '/orders/:orderId', component: OrderDetailsPage })}
                {pageRoute({ path: '/orders', component: OrdersPage })}
                {/* Old Orders Page routes */}
                {pageRoute({
                    path: '/visit-orders/:visitOrderId',
                    component: VisitOrderDetailsPage,
                })}
                {pageRoute({ path: '/visit-orders', component: VisitOrdersPage })}
                {/*
                Q: Why do we prefix the task details route with `/clinicians/...`?
                A: So that the sidebar label for "Clinicians" is highlighted when on the task
                   details page. The task detail page is only gotten to via a Clinician details
                   page, and kind of belongs semantically in the "Clinicians" sidebar group of
                   pages.
                */}
                {pageRoute({ path: '/clinicians/tasks/:taskId', component: TaskDetailsPage })}
                {pageRoute({ path: '/clinicians/:clinicianId', component: ClinicianDetailsPage })}
                {pageRoute({
                    path: '/clinicians',
                    component: hasNewCliniciansList ? CliniciansPage : SupplyPage,
                })}
                {pageRoute({ path: '/schedules/*', component: ClinicianSchedulesPage })}
                {pageRoute({ path: '/territories', component: TerritoriesPage })}
                {pageRoute({ path: '/services', component: DemandPage })}
                {pageRoute({ path: '/service-areas/create', component: CreateServiceAreaPage })}
                {pageRoute({
                    path: '/service-areas/:serviceAreaId',
                    component: EditOrViewServiceAreaPage,
                })}
                {pageRoute({ path: '/service-areas', component: ServiceAreasPage })}
                {pageRoute({ path: '/blocks', component: BlocksPage })}
                {pageRoute({ path: '/reports', component: ReportsPage })}
                {pageRoute({ path: '/insights', component: InsightsPage })}
                {pageRoute({ path: '/user-settings/*', component: UserSettingsPage })}
                {pageRoute({ path: '/settings', component: SettingsPage })}
                {pageRoute({ path: '/patient-intake', component: IntakeCapacityPage })}
                {/* Some development-only test paths (authenticated). */}
                {pageRoute({
                    path: '/.../map',
                    component: GoogleMapTestingShimPage,
                    condition: isLocal,
                })}
                {/* formerly isLocal */}
                {pageRoute({ path: '/.../scratch', component: ScratchPage })}
                {pageRoute({
                    path: '/.../query',
                    component: QueryTestingShimPage,
                    condition: isLocal,
                })}
                {/* Some development and production test paths (authenticated). */}
                {pageRoute({ path: '/.../sentry', component: SentryTestingShimPage })}
                {pageRoute({
                    path: '/.../visits/:visitId/travel-path-debug',
                    component: VisitTravelPathDebugPage,
                })}
                {/* This path is hard-coded into the backend. */}
                {pageRoute({ path: '/.../mangledb', component: MangleDbPage })}
            </Route>
            {/* Fallback for no matching route. */}
            <Route path='*' element={<NotFoundPage />} />
        </Routes>
    );
};

export const UnAuthedOnlyRoutes = () => {
    return (
        <Routes>
            {publicRoutes()}
            {/* Login pages (unauthenticated). */}
            {pageRoute({ path: '/sign-in', component: LoginPage })}
            {pageRoute({ path: '/sso', component: SSOLoginPage })}
            {pageRoute({ path: '/sso-callback', component: SSOCallbackPage })}
            {/* Any path that isn't an explicitly public route pushes a logged-out
                user to sign-in. This also enforces the redirect on sign-out, when
                AuthedRoutes unmounts and the current authed path no longer matches
                anything here. */}
            <Route path='*' element={<RedirectToSignin />} />
        </Routes>
    );
};

/* Define the app routes, and return the current app page. */
export const RootRoutes = () => {
    const authState = useAuthStateValue();
    const isAuthenticated = !!authState;
    const setHasHeldSession = useSetAtom(hasHeldSessionAtom);

    useEffect(() => {
        if (isAuthenticated) {
            setHasHeldSession(true);
        }
    }, [isAuthenticated, setHasHeldSession]);

    if (!authState) {
        return <UnAuthedOnlyRoutes />;
    }

    return (
        <>
            <LastAuthedPageRecorder userUuid={authState.uuid} />
            <AuthedRoutes />
        </>
    );
};
